import Decimal from "decimal.js-light";

type StopWithLoadmeters = { loadmeters: number | string | null };

const lmOf = (stop: StopWithLoadmeters) =>
  new Decimal(stop.loadmeters || 0);

const sumLm = (stops: StopWithLoadmeters[]) =>
  stops.reduce((acc, s) => acc.add(lmOf(s)), new Decimal(0));

// When the org hides loadmeters, the dialog neither shows the per-stop fields
// nor runs the lmMatch check, but the server still requires total pickup
// loadmeters to equal total dropoff loadmeters. A dropoff added while other
// dropoffs exist starts at 0 (see addStop in StopList), so deleting the
// dropoff that carried the loadmeters and re-adding it (e.g. to reorder)
// leaves the totals mismatched with no way for the user to see or fix it, and
// the save is rejected with ERR_LOADMETERS_MISMATCH.
//
// Rebalances the dropoffs to the pickup total: the first dropoff absorbs the
// difference, or carries the whole total if absorbing it would go negative.
// Matching totals are left untouched.
export const reconcileHiddenDropoffLoadmeters = <T extends StopWithLoadmeters>(
  pickups: StopWithLoadmeters[],
  dropoffs: T[]
): T[] => {
  if (!pickups.length || !dropoffs.length) {
    return dropoffs;
  }
  const pickupTotal = sumLm(pickups);
  if (pickupTotal.eq(sumLm(dropoffs))) {
    return dropoffs;
  }
  const [first, ...rest] = dropoffs;
  const firstLm = pickupTotal.minus(sumLm(rest));
  if (firstLm.gte(0)) {
    return [{ ...first, loadmeters: firstLm.toNumber() }, ...rest];
  }
  return [
    { ...first, loadmeters: pickupTotal.toNumber() },
    ...rest.map((d) => ({ ...d, loadmeters: 0 })),
  ];
};
